import React from 'react';

import type { UUID } from '@aline/common';
import { Select } from '@aline/common';
import { ButtonBase, Divider, Typography } from '@mui/material';
import { remove, update } from 'ramda';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { AnswerOptionInput } from './AnswerOptionInput';

const useStyles = makeStyles<StyleProps>()((theme, { disabled }) => ({
  addOptionButton: {
    justifyContent: 'unset',
    padding: theme.spacing(2, 2),
    width: '100%',
  },
  addOptionText: {
    ...theme.appTypography.buttonSmall,
    color: disabled ? theme.appColors.buttonDisabled : theme.appColors.primary,
  },
}));

interface StyleProps {
  disabled?: boolean;
}

export interface AnswerOptionItem {
  optionText: string;
  id?: UUID | null;
}
export type AnswerOptionItemVariant =
  | Apollo.FieldVariant.Checkbox
  | Apollo.FieldVariant.Dropdown
  | Apollo.FieldVariant.Radiobutton
  | Apollo.MetadataVariant.Checkbox
  | Apollo.MetadataVariant.Options
  | Apollo.MetadataVariant.RadioOptions;

interface AnswerOptionsListProps<
  T extends AnswerOptionItem,
> extends React.HTMLAttributes<HTMLElement> {
  onAddOption: (event: React.MouseEvent) => void;
  onDeleteOption: (newOptions: T[], deletedOption: T, deletedIndex: number) => void;
  onUpdateOption: (newOptions: T[], updatedOption: T) => void;
  options: T[];
  variant: AnswerOptionItemVariant;
  defaultAnswerIndex?: number;
  disabled?: boolean;
}

export function AnswerOptionsList<T extends AnswerOptionItem>(
  props: AnswerOptionsListProps<T>
): JSX.Element {
  const {
    className,
    defaultAnswerIndex,
    disabled,
    onAddOption,
    onDeleteOption,
    onUpdateOption,
    options,
    variant,
  } = props;
  const { classes } = useStyles({ disabled });

  return (
    <div className={className} role="list">
      {options.map((option, index) => {
        return (
          <AnswerOptionInput
            // Note: Using option.id as the key (if it exists) fixes an issue with initializing the input when used with Formik.
            key={option.id ?? index}
            defaultAnswerIndex={defaultAnswerIndex}
            disabled={disabled}
            index={index}
            option={option}
            variant={variant}
            onDelete={(answerOption, index) => {
              // Make sure there is always at least 1 option in the list.
              if (options.length === 1) return;

              const newOptions = remove(index, 1, options);

              onDeleteOption(newOptions, answerOption, index);
            }}
            onUpdate={(answerOption, index) => {
              const newOptions = update(index, answerOption, options);

              onUpdateOption(newOptions, answerOption);
            }}
          />
        );
      })}
      {options.length > 0 && <Divider variant="fullWidth" />}
      <ButtonBase
        className={classes.addOptionButton}
        data-cy={Select.document.editor.rightDrawer.fieldDetailPanel.addOptionButton}
        disabled={disabled}
        onClick={onAddOption}
      >
        <Typography className={classes.addOptionText}>Add option</Typography>
      </ButtonBase>
      <Divider variant="fullWidth" />
    </div>
  );
}
